import type { ColumnDef } from '@tanstack/react-table';
import { Eye, EyeOff, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { HistoryButton } from '@/features/audit-log';
import { DataTableColumnHeader } from '@/shared/components/data-table/data-table-column-header';
import { StatusBadge } from '@/shared/components/status-badge';

import type { Category } from '../types/category.types';

/**
 * Category columns. Sortable column ids are the list's sort keys (`nameEn`,
 * `sortOrder`), sorted by the Admin API — the table never sorts rows itself.
 * `sortOrder` is the storefront tab position, changed in "Storefront order".
 */
export function createCategoryColumns({
  canManage,
  onEdit,
  onToggleActive,
  onDelete,
}: {
  canManage: boolean;
  onEdit: (category: Category) => void;
  onToggleActive: (category: Category) => void;
  onDelete: (category: Category) => void;
}): ColumnDef<Category, unknown>[] {
  return [
    {
      accessorKey: 'nameEn',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Category" />,
      enableHiding: false,
      meta: { label: 'Category' },
      cell: ({ row }) => (
        <span className="font-medium">
          {row.original.nameEn ?? <span className="text-muted-foreground">—</span>}
        </span>
      ),
    },
    {
      id: 'nameAr',
      header: 'Arabic name',
      enableSorting: false,
      meta: { label: 'Arabic name' },
      cell: ({ row }) =>
        row.original.nameAr ? (
          <span dir="rtl">{row.original.nameAr}</span>
        ) : (
          <StatusBadge variant="warning">Missing</StatusBadge>
        ),
    },
    {
      id: 'isActive',
      header: 'Storefront',
      enableSorting: false,
      size: 110,
      meta: { label: 'Storefront' },
      cell: ({ row }) => (
        <StatusBadge variant={row.original.isActive ? 'success' : 'neutral'}>
          {row.original.isActive ? 'Active' : 'Inactive'}
        </StatusBadge>
      ),
    },
    {
      accessorKey: 'sortOrder',
      // Position 1 first: TanStack would start a numeric column descending.
      sortDescFirst: false,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Storefront position" align="end" />
      ),
      size: 120,
      meta: { label: 'Storefront position' },
      // "—": no order saved yet (or created since) — shown after the ordered ones.
      cell: ({ row }) => (
        <div className="text-end tabular-nums">{row.original.sortOrder ?? '—'}</div>
      ),
    },
    {
      id: 'brands',
      header: () => <div className="text-end">Brands</div>,
      enableSorting: false,
      size: 140,
      meta: { label: 'Brands' },
      cell: ({ row }) => (
        <div className="text-end tabular-nums">
          <Link
            to={`/brands?categoryId=${row.original.id}`}
            className="text-primary hover:underline"
            aria-label={`${row.original.brandCount} brands in ${row.original.nameEn ?? 'this category'}`}
          >
            {row.original.brandCount}
          </Link>
          <span className="text-muted-foreground"> ({row.original.activeBrandCount} active)</span>
        </div>
      ),
    },
    {
      id: 'actions',
      header: '',
      size: 80,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <span className="flex justify-end gap-1">
          <HistoryButton
            entityType="Category"
            entityId={String(row.original.id)}
            name={row.original.nameEn ?? `category ${row.original.id}`}
          />
          {canManage && (
            <CategoryRowActions
              category={row.original}
              onEdit={onEdit}
              onToggleActive={onToggleActive}
              onDelete={onDelete}
            />
          )}
        </span>
      ),
    },
  ];
}

function CategoryRowActions({
  category,
  onEdit,
  onToggleActive,
  onDelete,
}: {
  category: Category;
  onEdit: (category: Category) => void;
  onToggleActive: (category: Category) => void;
  onDelete: (category: Category) => void;
}) {
  const label = category.nameEn ?? `category ${category.id}`;
  // Only an unused category can be deleted; the Admin API re-checks at delete time.
  const inUse = category.brandCount > 0;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="size-7" aria-label={`Actions for ${label}`}>
          <MoreHorizontal className="size-3.5" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuItem onSelect={() => onEdit(category)} className="gap-2 text-xs">
          <Pencil className="size-3.5" aria-hidden />
          Edit names
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => onToggleActive(category)}
          className="gap-2 text-xs"
          variant={category.isActive ? 'destructive' : 'default'}
        >
          {category.isActive ? (
            <>
              <EyeOff className="size-3.5" aria-hidden />
              Deactivate
            </>
          ) : (
            <>
              <Eye className="size-3.5" aria-hidden />
              Activate
            </>
          )}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => onDelete(category)}
          disabled={inUse}
          className="gap-2 text-xs"
          variant="destructive"
        >
          <Trash2 className="size-3.5" aria-hidden />
          {inUse ? (
            <span className="flex flex-col">
              Delete
              <span className="text-muted-foreground text-[11px]">
                In use by {category.brandCount} brand{category.brandCount === 1 ? '' : 's'}
              </span>
            </span>
          ) : (
            'Delete'
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
